import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import ContentWrapper from '../Layout/ContentWrapper';
import { Alert } from 'reactstrap';
import Now from '../Common/Now';
import moment from 'moment/min/moment-with-locales';
import * as statisticsService from './Services/statisticsService';
import HelpModal from './Components/Modals/helpModal';
import './Components/css/dashboard.css';
import Tour from 'reactour';
import swal from 'sweetalert';
import * as userService from './Services/userService';
import * as HelpMessages from './Services/helpMessages';
import { getMineOrSelected, hasAdminRole } from './Services/utils';
import { SpinnerLoader } from './Components/shared/spinnerLoader';
import CompetencesCard from './Components/DashBoard/dashboardCompetencesCard';
import DashboardJobsCard from './Components/DashBoard/dashboardJobsCard';
import DashboardApplicationsCard from './Components/DashBoard/dashboardApplicationsCard';
import DashboardApplicantsCard from './Components/DashBoard/dashboardApplicantsCard';
import useStatisticStore from '../../stores/statisticsStore';
import useCompanyStore from '../../stores/companyStore';
import useAuthStore from '../../stores/authStore';
import useUsersStore from '../../stores/usersStore';

const Dashboard = ({ t, history }) => {
  const [showHelpApplicantsModal, setShowHelpApplicantsModal] = useState(false);
  const [showHelpApplicationsModal, setShowHelpApplicationsModal] = useState(false);
  const [showHelpJobsModal, setShowHelpJobsModal] = useState(false);
  const [showHelpCompetencesModal, setShowHelpCompetencesModal] = useState(false);
  const [showHelpDashboardModal, setShowHelpDashboardModal] = useState(false);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [showSettingsGuideModal, setShowSettingsGuideModal] = useState(false);
  const [statisticsData, setStatisticsData] = useState(null);

  // Zustand store for statistics
  const statistic = useStatisticStore((state) => state.statistic);
  const setStatistic = useStatisticStore((state) => state.setStatistic);
  const company = useCompanyStore((state) => state.company);
  const fetchCompany = useCompanyStore((state) => state.fetchCompany);

  const user = useAuthStore((state) => state.user);
  const setUserDataSettings = useAuthStore((state) => state.setUserDataSettings);
  const GLOBAL_USER_Settings = user?.settings;
  const isAdmin = hasAdminRole(user);

  const users = useUsersStore((state) => state.users);
  const fetchUsers = useUsersStore((state) => state.fetchUsers);

  // Reactour steps
  const steps = [
    {
      selector: '[data-tut="reactour__company"]',
      content: () => HelpMessages.getReactour__company(t.language),
    },
    {
      selector: '[data-tut="reactour__jobs"]',
      content: () => HelpMessages.getReactour__jobs(t.language),
    },
    {
      selector: '[data-tut="reactour__job_info"]',
      content: () => HelpMessages.getReactour__job_info(t.language),
    },
    {
      selector: '[data-tut="reactour__applicants"]',
      content: () => HelpMessages.getReactour__applicants(t.language),
    },
    {
      selector: '[data-tut="reactour__create_applicant"]',
      content: () => HelpMessages.getReactour__create_applicant(t.language),
    },
    {
      selector: '[data-tut="reactour__applications"]',
      content: () => HelpMessages.getReactour__applications(t.language),
    },
    {
      selector: '[data-tut="reactour__comments"]',
      content: () => HelpMessages.getReactour__comments(t.language),
    },
    {
      selector: '[data-tut="reactour__calendar"]',
      content: () => HelpMessages.getReactour__calendar(t.language),
    },
    {
      selector: '[data-tut="reactour__competences"]',
      content: () => HelpMessages.getReactour__competences(t.language),
    },
    {
      selector: '[data-tut="reactour__walkthrough"]',
      content: () => HelpMessages.getReactour__walkthrough(t.language),
    },
  ];

  useEffect(() => {
    if (!company) {
      fetchCompany();
    }

    if (!users || users.length === 0) {
      fetchUsers();
    }
  }, []);

  useEffect(() => {
    const fetchStatistics = async () => {
      let mineOrAllSelected = getMineOrSelected();
      const { data: fetchedStatistics } = await statisticsService.getStatistics(mineOrAllSelected);

      // Update Zustand store
      setStatistic(fetchedStatistics);

      // Update local state
      setStatisticsData(fetchedStatistics);
    };

    fetchStatistics();
    // eslint-disable-next-line
  }, [setStatistic]);

  useEffect(() => {
    if (GLOBAL_USER_Settings) {
      let welcomeMsg = GLOBAL_USER_Settings.find(({ name }) => name === 'ShowWelcomeMessage');
      if (welcomeMsg) {
        if (welcomeMsg.value === '1') {
          showWelcomeMessage(welcomeMsg);
        } else {
          checkSettingsGuideModalVisibility();
        }
      } else {
        showWelcomeMessage({ name: 'ShowWelcomeMessage', value: 0 });
        checkSettingsGuideModalVisibility();
      }
    } else {
      checkSettingsGuideModalVisibility();
    }
    // eslint-disable-next-line
  }, [GLOBAL_USER_Settings, statistic]);

  const showWelcomeMessage = (welcomeMsg) => {
    swal({
      title: t('dashboard.THANKS_FOR_ACTIVATING_ACCOUNT'),
      text: t('dashboard.WELCOME_MESSAGE'),
      icon: 'success',
      buttons: {
        cancel: {
          text: t('common.CLOSE'),
          visible: true,
          closeModal: true,
          value: 'closing',
        },
        confirm: {
          text: t('dashboard.START_WALKTHROUGH'),
          value: 'true',
          visible: true,
          closeModal: true,
        },
        dontShowAgain: {
          text: t('dashboard.DONT_SHOW_AGAIN'),
          value: 'dontShowAgain',
          color: 'red',
        },
      },
      dangerMode: false,
    }).then((value) => {
      switch (value) {
        case 'true':
          setIsTourOpen(true);
          break;
        case 'dontShowAgain':
          updateUserWelcomeMessage(welcomeMsg);
          break;
        case 'closing':
          checkSettingsGuideModalVisibility();
          break;
        default:
      }
    });
  };

  const updateUserWelcomeMessage = (welcomeMsg) => {
    checkSettingsGuideModalVisibility();

    let updateValue = [{ id: welcomeMsg.id, value: 0, name: welcomeMsg.name }];
    userService.updateUserSettings(updateValue).then((response) => {
      setUserDataSettings(response.data);
    });
  };

  const checkSettingsGuideModalVisibility = () => {
    if (statistic?.userFirstName && statistic?.userLastName && statistic?.companyName) {
      setShowSettingsGuideModal(false);
    } else {
      setShowSettingsGuideModal(true);
    }
  };

  const handleKeyPress = (event, source, value) => {
    if (event.key === 'Enter') {
      const routes = {
        applicantsName: `/applicants?name=${value}`,
        applicantsSecondName: `/applicants?secondName=${value}`,
        applicationJobTitle: `/applications?jobTitle=${value}`,
        applicationApplicantName: `/applications?applicantName=${value}`,
        applicationApplicantLastName: `/applications?applicantLastName=${value}`,
        jobName: `/jobs?jobName=${value}`,
        competenceName: `/competences?competenceName=${value}`,
      };
      if (routes[source]) {
        history.push(routes[source]);
      }
    }
  };

  const toggleHelpModal = (origin) => {
    if (origin === 'applicantsHelp') setShowHelpApplicantsModal((v) => !v);
    else if (origin === 'applicationsHelp') setShowHelpApplicationsModal((v) => !v);
    else if (origin === 'jobsHelp') setShowHelpJobsModal((v) => !v);
    else if (origin === 'competencesHelp') setShowHelpCompetencesModal((v) => !v);
    else if (origin === 'dashboardHelp') setShowHelpDashboardModal((v) => !v);
  };

  const toggleSettingsGuideModal = () => {
    setShowSettingsGuideModal((v) => !v);
  };

  const goToCompanySettings = () => {
    setShowSettingsGuideModal(false);
    history.push('/settings/companyProfile');
  };

  const goToUserSettings = () => {
    setShowSettingsGuideModal(false);
    history.push('/settings/profile');
  };

  const handleClick = (urlLocation) => {
    history.push(urlLocation);
  };

  if (!statisticsData) {
    return <SpinnerLoader />;
  }

  return (
    <ContentWrapper>
      <Tour steps={steps} isOpen={isTourOpen} onRequestClose={() => setIsTourOpen(false)} />
      <div className="content-heading">
        <div>
          {t('menu.DASHBOARD')}
          <em
            id="startWalkthrough"
            data-tut="reactour__walkthrough"
            style={{ cursor: 'pointer' }}
            className="fa fa-play text-primary mx-2"
            onClick={() => setIsTourOpen(true)}
          />
          {/* <HelpModal
            purpose="dashboardHelp"
            showModal={showHelpDashboardModal}
            onToggle={() => toggleHelpModal('dashboardHelp')}
          /> */}
          <small>{t('dashboard.WELCOME_BETA')}</small>
        </div>
      </div>
      <div className="row">
        <div className="col-xl-1" />
        <div className="col-xl-3">
          <div
            className="card text-center statistic-data"
            style={{ cursor: 'pointer' }}
            onClick={() => handleClick('/settings/companyProfile')}
            data-tut="reactour__company"
          >
            <div className="row align-items-center justify-content-center py-3">
              <div className="col-4">
                {company && company.logoPhotoUrl ? (
                  <img className="img-fluid img-thumbnail" src={company.logoPhotoUrl} alt="Company Logo" />
                ) : (
                  <em className="fa fa-address-card fa-4x text-muted"></em>
                )}
              </div>
              <div className="col-8">
                <h3 className="mb-2">{statisticsData?.companyName || t('dashboard.NO_COMPANY_NAME')}</h3>
                {/* {statisticsData && statisticsData.companyName && statisticsData.companyUrl ? (
                  <a href={statisticsData.companyUrl} className="text-primary">
                    <i className="fa fa-globe"></i> {t('dashboard.WEBPAGE')}
                  </a>
                ) : (
                  <Alert color="warning" className="mt-2">
                    {t('dashboard.ADD_INFORMATION')}
                  </Alert>
                )} */}
              </div>
            </div>
          </div>
        </div>
        {/* Action centre card */}
        <div className="col-xl-4">
          <div className="card bg-purple border-0" data-tut="reactour__comments">
            <div className="card-footer bg-gray-dark bt0 clearfix btn-block">
              <span>{t('common.ACTION_CENTRE')}</span>
            </div>
            <div className="d-flex flex-nowrap justify-content-center">
              <div style={{ paddingTop: '12px', paddingRight: '24px' }}>
                <em className="fa fa-tasks fa-4x"></em>
              </div>
              <div
                style={{ cursor: 'pointer', paddingRight: '12px' }}
                onClick={() => handleClick('/applicants/commentsAndHistoryTimeline?latest=true')}
              >
                <div className="text-lg text-center">{statisticsData?.numComments}</div>
                <p>{t('common.COMMENTS')}</p>
              </div>
              <div
                style={{ cursor: 'pointer', paddingRight: '12px' }}
                onClick={() => handleClick('/applicants?onlyNew=1')}
              >
                <div className="text-lg text-center">{statisticsData?.newApplicants}</div>
                <p>{t('applicants.NEW_APPLICANTS')}</p>
              </div>
              <div
                style={{ cursor: 'pointer', paddingRight: '12px' }}
                onClick={() => handleClick('/applications?onlyNew=1')}
              >
                <div className="text-lg text-center">{statisticsData?.newApplications}</div>
                <p>{t('applications.NEW_APPLICATIONS')}</p>
              </div>
              <div style={{ cursor: 'pointer', paddingRight: '12px' }} onClick={() => handleClick('/cldr')}>
                <div className="text-lg text-center">{statisticsData?.numCalendarEvents}</div>
                <p>{t('dashboard.MEETINGS')}</p>
              </div>
            </div>
          </div>
        </div>
        {/* Date card */}
        <div className="col-xl-3">
          <div
            className="card flex-row align-items-center align-items-stretch"
            style={{ cursor: 'pointer' }}
            onClick={() => handleClick('/cldr')}
            data-tut="reactour__calendar"
          >
            <div className="col-4 d-flex align-items-center bg-green justify-content-center rounded-left">
              <div className="text-center">
                {moment().format('MMMM')}
                <br />
                <Now format="D" className="h2 mt0" />
              </div>
            </div>
            <div className="col-8 py-2 text-center rounded-right">
              <Now format="H:mm:ss" className="h2 mt0 mr-sm" />
              <br />
              <div className="badge badge-warning">
                {t('dashboard.EVENTS_TODAY')} {statisticsData?.numCalendarEvents}
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-1" />
      </div>
      <div className="row">
        {/* APPLICANTS card */}
        <div className="col-xl-1" />
        <div className="col-xl-5">
          <DashboardApplicantsCard
            statisticsData={statisticsData}
            onSearch={(value, source) => handleKeyPress({ key: 'Enter' }, source, value)}
            onNavigate={(action, param) => {
              if (action === 'toggleHelpModal') toggleHelpModal(param);
              else if (action === 'toggleSettingsGuideModal') toggleSettingsGuideModal();
              else if (action === 'goToCompanySettings') goToCompanySettings();
              else if (action === 'goToUserSettings') goToUserSettings();
              else handleClick(action);
            }}
            isAdmin={isAdmin}
          />
        </div>
        <div className="col-xl-5">
          <DashboardApplicationsCard
            showModal={showHelpApplicationsModal}
            statisticsData={statisticsData}
            onSearch={(value, source) => handleKeyPress({ key: 'Enter' }, source, value)}
            onNavigate={(action, param) => {
              if (action === 'toggleHelpModal') toggleHelpModal(param);
              else handleClick(action);
            }}
            isAdmin={isAdmin}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xl-1" />
        <div className="col-xl-5">
          <DashboardJobsCard
            statisticsData={statisticsData}
            showModal={showHelpJobsModal}
            onSearch={(value) => handleKeyPress({ key: 'Enter' }, 'jobName', value)}
            onNavigate={(action, param) => {
              if (action === 'toggleHelpModal') toggleHelpModal(param);
              else handleClick(action);
            }}
            isAdmin={isAdmin}
          />
        </div>
        <div className="col-xl-5">
          <CompetencesCard
            statisticsData={statisticsData}
            showModal={showHelpCompetencesModal}
            onSearch={(value) => handleKeyPress({ key: 'Enter' }, 'competenceName', value)}
            onNavigate={(action, param) => {
              if (action === 'toggleHelpModal') toggleHelpModal(param);
              else handleClick(action);
            }}
          />
        </div>
        <div className="col-xl-1" />
      </div>
    </ContentWrapper>
  );
};

export default withTranslation('translations')(Dashboard);
